import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { ApiService } from './api.service';
import { LoggerService } from './logger.service';
import { UserService } from './user.service';
import { TransferService } from '../transfer/services/queue/transfer.service';
import { E2eeZipService } from '../transfer/services/download/zip/e2ee-zip.service';
import { SnackbarService } from '../shared/services/snackbar.service';
const INDEX_URL = '/index.html';
const HASHED_ASSET_REGEXP = /[\w-]+\.[a-f0-9]{8,}\.(?:js|css)/g;

@Injectable({
  providedIn: 'root',
})
export class AppVersionService {
  private baselineFingerprint: string | null = null;
  private updateAvailable = false;
  private promptShown = false;
  private pollTimer: ReturnType<typeof setInterval> | null = null;

  constructor(
    private apiService: ApiService,
    private transferService: TransferService,
    private e2eeZipService: E2eeZipService,
    private snackbarService: SnackbarService,
    private userService: UserService,
    private loggerService: LoggerService,
  ) {}

  public async start(): Promise<void> {
    if (this.pollTimer !== null) {
      return;
    }
    const interval = environment.versionPollInterval;
    if (!interval || interval <= 0) {
      return;
    }
    this.baselineFingerprint = await this.fetchFingerprint();
    this.pollTimer = setInterval(() => {
      this.tick();
    }, interval);
  }

  private async tick(): Promise<void> {
    if (!this.updateAvailable) {
      const current = await this.fetchFingerprint();
      if (current && this.baselineFingerprint && current !== this.baselineFingerprint) {
        this.updateAvailable = true;
        this.loggerService.info('[AppVersion] A new CP release was detected');
      }
    }
    if (this.updateAvailable) {
      this.maybePrompt();
    }
  }

  private maybePrompt(): void {
    if (this.promptShown) {
      return;
    }
    if (!this.userService.isAuthenticated()) {
      return;
    }
    if (this.hasActiveTransfers()) {
      return;
    }
    this.promptShown = true;
    this.stopPolling();
    this.showRefreshPrompt();
  }

  private hasActiveTransfers(): boolean {
    return this.transferService.hasActiveTransfers() || this.e2eeZipService.isProcessing();
  }

  private showRefreshPrompt(): void {
    this.snackbarService.showMessage('default', 'A new version of Sync is available.', {
      config: { autoClose: false },
      trailAdornmentConfig: {
        enabled: true,
        type: 'button',
        size: 'large',
        buttonConfig: {
          type: 'flat',
          color: 'neutral',
          label: 'Refresh',
          size: 'small',
          onClick: (): void => {
            this.reload();
          },
        },
      },
    });
  }

  private async fetchFingerprint(): Promise<string | null> {
    try {
      const html = await this.apiService.fetchText(`${INDEX_URL}?_=${this.now()}`);
      return this.fingerprint(html);
    } catch (err) {
      this.loggerService.warn('[AppVersion] Could not fetch index.html for version check');
      return null;
    }
  }

  /**
   * Fingerprint the content-hashed bundle refs in index.html; falls back to
   * the whitespace-normalized document when no hashes exist (e.g. dev builds).
   */
  private fingerprint(html: string): string {
    const hashedAssets = html.match(HASHED_ASSET_REGEXP);
    if (hashedAssets && hashedAssets.length) {
      return Array.from(new Set(hashedAssets)).sort().join('|');
    }
    return html.replace(/\s+/g, ' ').trim();
  }

  private stopPolling(): void {
    if (this.pollTimer !== null) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  }

  private reload(): void {
    window.location.reload();
  }

  private now(): number {
    return Date.now();
  }
}
